import _ from "lodash";
import { constants } from "../utils/constants";
import { format, getTimezoneOffset, utcToZonedTime } from "date-fns-tz";
import zipcelx from "zipcelx";

export const getHeaders = (array: Array<any>) => {
  return Object.keys(array[0]).map(header => ({
    value: header,
    text: _.capitalize(header),
    align: "left"
  }));
};

export const formatDates = (list: Array<any>) => {
  let keys = Object.keys(list[0]);
  let toFormat: Array<string> = [];
  constants.fieldsToFormat.forEach(constant => {
    if (keys.find(key => key === constant)) {
      toFormat.push(constant);
    }
  });

  let formatedList = list.map(item => {
    toFormat.map(field => {
      // Handle null/invalid dates
      if (
        item[field] === "0000-00-00" ||
        item[field] === "0000-00-00 00:00:00" ||
        !item[field]
      ) {
        item[field] = null;
        return;
      }

      // Date-only fields (not DATETIME): extract YYYY-MM-DD and parse as local midnight
      // to prevent timezone shift (e.g. 2018-05-14T00:00:00.000Z → May 13 in EST).
      if (field === "startDate" || field === "shiftDate") {
        const dateStr = item[field].toString();
        const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
        const localDate = dateMatch
          ? new Date(dateMatch[1] + 'T00:00:00')
          : new Date(dateStr);
        item[field] = format(localDate, "MMM dd, yyyy");
      } 
      // For other datetime fields, apply timezone conversion
      else if (item[field].length >= 19) {
        // Full datetime field with timezone
        const data = new Date(item[field]);
        const offset = getTimezoneOffset(currentTimeZone(), data);
        item[field] = format(new Date(data.getTime() - offset), "MMM dd, yyyy HH:mm:ss");
      } 
      // Default: format without timezone conversion
      else {
        item[field] = format(new Date(item[field]), "MMM dd, yyyy HH:mm:ss");
      }
    });
    return item;
  });
  return formatedList;
};

export const currentTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const dateToUTC = (date: string): string => {
  const offset = getTimezoneOffset(currentTimeZone()) + 3600000;
  const value = new Date(date).getTime() - offset;
  return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
};

export const dateToISO = (date: string): string => {
  return new Date(date).toISOString();
};

export const getXls = (
  list: Array<any>,
  headers: Array<{ value: string; type: string }>,
  filename: string
) => {
  let preparedList = list.map(item => {
    let values = Object.values(item).map(value => ({
      value,
      type: String(typeof value)
    }));
    return values;
  });
  preparedList.unshift(headers);
  zipcelx({
    filename: filename,
    sheet: {
      data: preparedList as any
    }
  });
};

export const convertValues = (
  data: Array<any>,
  options: { ffsStatus?: boolean } = {}
) => {
  const Type = {
    "0": "Hygienist",
    "1": "Assistant",
    "2": "Admin",
    "3": "Associate Dentist"
  };

  return data.map(user => {
    const converted = { ...user };
    if (options.ffsStatus && Object.prototype.hasOwnProperty.call(converted, "status")) {
      converted.status =
        typeof converted.currentStatusCode === "string" && converted.currentStatusCode.trim()
          ? converted.currentStatusCode
          : "FFS status unavailable";
    }
    if (Object.prototype.hasOwnProperty.call(converted, "type")) {
      converted.type = Type[converted.type] ?? converted.type;
    }
    return converted;
  });
};

export const formatNotes = (note: string | null) => {
  return note && note.length >= 1
    ? note
        .split(" ")
        .slice(0, 4)
        .join(" ") + "..."
    : "";
};

/**
 * Validates and normalizes timezone strings
 * Handles known timezone aliases and invalid timezones
 */
export const validateTimezone = (tz: string | null | undefined): string => {
  if (!tz || typeof tz !== 'string') {
    return currentTimeZone();
  }

  // Map known timezone aliases to valid IANA timezones
  // Europe/Kyiv was renamed from Europe/Kiev in 2022, but some systems may not recognize it
  const timezoneMap: Record<string, string> = {
    'Europe/Kyiv': 'Europe/Kiev', // Fallback for systems that don't recognize Kyiv
  };

  // First, try to validate the original timezone
  try {
    const testDate = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric'
    });
    formatter.format(testDate);
    // If it works, return the original
    return tz;
  } catch (error) {
    // If the original timezone fails, try the mapped version
    if (timezoneMap[tz]) {
      try {
        const testDate = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezoneMap[tz],
          hour: 'numeric'
        });
        formatter.format(testDate);
        return timezoneMap[tz];
      } catch (mapError) {
        // If mapped version also fails, fall back to UTC
        // eslint-disable-next-line no-console
        console.warn(`Invalid timezone "${tz}" and fallback "${timezoneMap[tz]}", using UTC`);
        return "UTC";
      }
    }
    // If no mapping exists and original fails, fall back to UTC
    // eslint-disable-next-line no-console
    console.warn(`Invalid timezone "${tz}", falling back to UTC`);
    return "UTC";
  }
};

export const abbreviationFromTimeZone = (timezone: string): string => {
  const validTz = validateTimezone(timezone);
  let toLocal = new Date();
  try {
    const formatToLocal = toLocal.toLocaleString("en-US", {
      timeZone: validTz,
      timeZoneName: "short"
    });
    const abbreviation = formatToLocal.split(" ");
    return abbreviation[abbreviation.length - 1];
  } catch (error) {
    return "UTC";
  }
};

export const formatInTimeZone = (
  date: string | Date,
  fmt: string,
  tz: string
) => {
  if (!date) return "-";
  const dateStr = typeof date === "string" ? date : "";
  if (dateStr.startsWith("0000-00-00")) return "-";

  const validTz = validateTimezone(tz);
  try {
    return format(utcToZonedTime(date, validTz), fmt, { timeZone: validTz });
  } catch (error) {
    try {
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return "-";
      return format(parsed, fmt);
    } catch {
      return "-";
    }
  }
};

export const isDST = (d: Date) => {
  let jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
  let jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== d.getTimezoneOffset();
};

export const objectInvert = (obj: object) => {
  const ret = {};
  Object.keys(obj).forEach(key => {
    ret[obj[key]] = key;
  });
  return ret;
};

export const deleteObjectFromArray = (preparedDataToSave: any, obj: any) => {
  const indexToDelete = preparedDataToSave.findIndex(item => item === obj);
  if (indexToDelete !== -1) {
    preparedDataToSave.splice(indexToDelete, 1);
    if (preparedDataToSave.length === 0) {
      return [];
    }
  }
  return preparedDataToSave;
};
